// Global packages or components
import {
  getMeta,
  buildMetaDescription,
  formatCategoryBreadcrumb,
  getErrorMessage,
  logRequestError,
  createProductCategoryTitles,
  getSchema,
  Schema,
  buildProductSchemaInput,
  hasValidProductSchema,
  portableTextToPlain,
  notFoundResponse,
} from '~/utils'
import { ProductSingle, Breadcrumbs } from '~/components'
import { useLoaderData } from '@remix-run/react'
import { redirect } from '@remix-run/node'
import {
  getProductDetails,
  getRelatedProducts,
  trackRecentlyViewed,
  recentlyViewed,
} from '~/.server'
import { getCategory, getProductEnrichment } from '~/sanity'
import { usePageInfo } from '~/hooks'

// Types
import type {
  MetaFunction,
  LoaderFunction,
  LoaderFunctionArgs,
} from '@remix-run/node'

// Route loader
export const loader: LoaderFunction = async ({
  request,
  params,
}: LoaderFunctionArgs) => {
  // Global data
  const env = process.env
  const url = new URL(request.url)
  const stockCode = params.code
  const slug = params.slug

  // Problem with URL
  if (!stockCode || !slug) {
    throw notFoundResponse('Missing route params')
  }

  try {
    // Fetch PDP data
    const product = await getProductDetails({
      stockCode,
    })

    // Product not found
    if (!product || !product?.slug) throw notFoundResponse('Product not found')

    // Slug mismatch: 301 to canonical, preserving query string
    if (product.slug !== slug) {
      const dest = new URL(`/hire/p/${stockCode}/${product.slug}`, url.origin)
      dest.search = url.search
      throw redirect(dest.pathname + dest.search, 301)
    }

    // Missing variants array
    const hasVariants = product?.variants && product.variants?.length > 0
    if (!hasVariants) throw notFoundResponse('Missing variants')

    // SEO data
    const metaTitle = product?.stringAttributes?.find(
      e => e.name === 'metaTitle'
    )
    const metaDescription = product?.stringAttributes?.find(
      e => e.name === 'metaDescription'
    )

    // Filter the category string attributes for the one we want
    const categoryAttribute = product?.stringAttributes.find(
      e => e.name === 'categories'
    )

    // Everything below needs the product only, so fetch in parallel to keep TTFB down
    const [categoryData, relatedProducts, enrichment] = await Promise.all([
      categoryAttribute?.value
        ? getCategory({
            dataset: env?.ENVIRONMENT,
            projectId: env?.SANITY_PROJECT,
            slug: categoryAttribute.value,
          })
        : null,
      categoryAttribute?.value
        ? getRelatedProducts({
            categorySlug: categoryAttribute.value,
            productType: 'bulk',
            excludeStockCode: stockCode,
          })
        : [],
      getProductEnrichment({
        dataset: env?.ENVIRONMENT,
        projectId: env?.SANITY_PROJECT,
        category: categoryAttribute?.value,
        stockCode,
      }),
    ])

    // Track this product view and read prior views for the carousel. Use the
    // first variant's stock code (not the parent URL code) so the carousel
    // search resolves a single variant per product. If it's missing, nothing
    // is added to the list.
    const {
      recentlyViewed: recentlyViewedCodes,
      updated: recentlyViewedUpdated,
    } = await trackRecentlyViewed(request, product.variants[0]?.stockCode)

    return Response.json(
      {
        path: url?.pathname,
        recentlyViewed: recentlyViewedCodes,
        seo: {
          metaTitle: metaTitle?.value ?? product.name,
          metaDescription: buildMetaDescription(
            metaDescription?.value,
            product?.description
          ),
        },
        product: product,
        relatedProducts,
        enrichment: enrichment,
        categoryBreadcrumb:
          categoryData && categoryAttribute
            ? formatCategoryBreadcrumb({
                title: categoryData.title,
                type: categoryData.type,
                parent: categoryData.parent,
                slug: categoryAttribute.value,
              })
            : null,
      },
      {
        headers: {
          'Set-Cookie': await recentlyViewed.serialize(recentlyViewedUpdated),
        },
      }
    )
  } catch (error) {
    // If it was an intentional HTTP response, keep it
    if (error instanceof Response) {
      // Optional: don't treat redirects as errors in logging
      if (error.status >= 300 && error.status < 400) throw error

      const detail = error.headers.get('X-Error-Detail')
      logRequestError(
        'PDP',
        detail ? `${error.status}: ${detail}` : `${error.status}`
      )
      throw error
    }

    // Otherwise it's a real failure
    logRequestError('PDP', getErrorMessage(error))
    throw new Response('Not Found', { status: 404 })
  }
}

// Route meta
export const meta: MetaFunction = ({ matches, data }) => {
  return getMeta({
    matches,
    data,
  })
}

// Route view
export default function Index() {
  const {
    path,
    product,
    enrichment,
    categoryBreadcrumb,
    seo,
    recentlyViewed,
    relatedProducts,
  } = useLoaderData<typeof loader>()

  // Generated category titles from the category path
  const categories = createProductCategoryTitles(product)

  usePageInfo({
    pageType: 'Product',
    pageTitle: product?.name,
    categoryType: 'Hire',
    categoryParentTitle: categories ? categories[0] : undefined,
    categoryTitle: categories ? categories[1] : undefined,
  })

  const productSchemaInput = buildProductSchemaInput(product, path, seo)

  const jsonLd = [
    getSchema({
      type: 'page',
      page: {
        ...seo,
        redirect: path,
      },
    }),
  ]

  // An unpriced product would fail validation as a Product, so it carries none
  if (hasValidProductSchema(productSchemaInput)) {
    jsonLd.push(getSchema({ type: 'product', product: productSchemaInput }))
  }

  // FAQ markup moved out of the component into JSON-LD alongside the rest
  if (enrichment?.faqs?.length) {
    jsonLd.push(
      getSchema({
        type: 'faq',
        faqs: enrichment.faqs.map(
          (e: { question: string; answer: unknown }) => ({
            question: e.question,
            answer: portableTextToPlain(e.answer),
          })
        ),
      })
    )
  }

  return (
    <main>
      <Schema data={jsonLd} />
      <Breadcrumbs
        path={path}
        insertBeforePage={categoryBreadcrumb}
        removePrevious
      />
      <ProductSingle
        {...product}
        faqs={enrichment?.faqs}
        blogPosts={enrichment?.blogPosts}
        recentlyViewed={recentlyViewed}
        relatedProducts={relatedProducts}
      />
    </main>
  )
}
